/* eslint-disable react/prop-types */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { auth } from '../services/firebase'
import { getAllSites, getUserDetails, loginUser, logoutUser } from '../model'

import { SiteViewModel, UserInfo } from './ViewModel'
import { User } from 'firebase/auth'

/**
 * Combine Firebase Auth User and App User Info
 */
type AppUser = User & UserInfo

interface AuthContextInterface {
  currentUser: AppUser
  /**
   * List of sites for a specific user with limit access of regions
   */
  sites: SiteViewModel[]
  /**
   * @summary Signs in a user via their email and password.
   * @param {string} email The user's email address.
   * @param {string} password The user's password.
   * @returns Promise
   */
  login: (email: string, password: string) => void
  /**
   * @summary Signs out the currently authenticated user.
   * @returns Promise
   */
  logout: () => void

  isAdmin: boolean

  /**
   * @summary Loading state when fetching user
   * @description This often appears when user reload the pages
   *
   */
  loading: boolean
}

const AuthContext = createContext<AuthContextInterface | null>(null)

/**
 * @returns currentUser => The currently logged in user.
 * @returns register(name, phone, email, password) => Function for creating a new member.
 * @returns login(email, password) => Funtion for signing a user into the application.
 * @returns logout() => Funtion for signing a user out.
 */
export const useAuth = () => useContext(AuthContext)

/**
 *
 * @summary Wrapper which provides access to the auth context.
 *
 * @description
 * Using this context as a wrapper around the app's router
 * allows routes to access currentUser and authentication methods.
 */
export function AuthProvider({ children }: { children?: React.ReactNode }) {
  const [currentUser, setcurrentUser] = useState<AppUser>(null)
  const [loading, setLoading] = useState(false)
  const [sites, setSites] = useState<SiteViewModel[]>([])

  const isAdmin =
    currentUser && currentUser.role && currentUser.role.type == 'admin'

  const login = async (email: string, password: string) =>
    loginUser(email, password)

  const logout = async () => logoutUser()

  /**
   * When a user signs in, their details are set in the { currentUser } object.
   * When a user signs out, { currentUser } is set to null.
   */
  useEffect(() => {
    setLoading(true)
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDoc = await getUserDetails(user.uid)
        const userDetails: AppUser = { ...user, ...userDoc }
        setcurrentUser(userDetails)

        const regions_accessed = userDoc.role.regions_accessed ?? []
        const sitesData = await getAllSites()

        // Filtering out specified regions for a user
        if (regions_accessed.length === 0) {
          setSites(sitesData)
        } else {
          const filteredSitesData = sitesData.filter((site) => {
            return regions_accessed.includes(site.region)
          })
          setSites(filteredSitesData)
        }
      } else {
        setcurrentUser(null)
        setSites([])
      }

      setLoading(false)
    })
    return unsubscribe
  }, [])

  const contextValues = useMemo<AuthContextInterface>(
    () => ({
      currentUser,
      sites,
      login,
      logout,
      isAdmin,
      loading,
    }),
    [currentUser, loading, sites.length]
  )

  return (
    <AuthContext.Provider value={contextValues}>
      {children}
    </AuthContext.Provider>
  )
}

export function MemberAuth({ children }: { children?: React.ReactNode }) {
  const { currentUser } = useAuth()

  if (currentUser && currentUser.role && currentUser.role.type == 'staff') {
    return children
  }

  return <Navigate to="/" replace />
}

export function AdminRoute() {
  const { currentUser } = useAuth()

  if (currentUser && currentUser.role && currentUser.role.type == 'admin') {
    return <Outlet />
  }

  return <Navigate to="/" />
}

export function SiteAuthRoute() {
  const { siteId } = useParams()
  const { sites } = useAuth()

  // NOTE: Unable to use await in useEffect to fetchSite
  // NOTE: Unable to use custom hooks to fetchSite

  // Make sure
  const site = useMemo(() => {
    return sites.find((site) => site.id === siteId)
  }, [siteId])

  if (site) {
    return <Outlet />
  }
  return <Navigate to="/collection" />
}
